import "./PickupMarkerPopup.css";
import { memo, useEffect } from "react";
import { useSelector } from "react-redux";
import clsx from "clsx";
import { Divider } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Popup } from "react-mapbox-gl";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import { palette } from "../../../theme/palette";

import { pickupVolumesSelector } from "../../../reducers/matrixProfile/selectors";
import { getParcels } from "../../../utils/routes";

import { Text } from "../../Base";
import { View } from "../../View";
import { CloseIcon } from "../../Icons/Close";
import { ParcelIcon } from "../../Icons";

const useStyles = makeStyles({
  popup: {
    color: palette.white,
    width: "245px",
    maxHeight: "280px",
    paddingBottom: "50px",
    paddingRight: "5px",
    zIndex: "4!important",
    borderColor: palette.typography.black.high,
    borderRadius: "10px",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "& .mapboxgl-popup-content": {
      background: "transparent",
      padding: "0px !important",
    },
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  popupContents: {
    width: "240px",
    maxHeight: "255px",
    background: palette.lowTranslucentTrueBlack,
    padding: "10px 10px 15px 10px",
    borderRadius: "10px",
  },
  detailsContainerTopRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: "18px",
  },
  detailsContainerCloseIcon: {
    height: "12px",
    cursor: "pointer",
  },
  postcode: {
    fontSize: "16px",
  },
  postcodeSpace: {
    height: "8px",
  },
  route: {
    fontSize: "18px",
    marginTop: "10px",
    marginBottom: "0px",
    color: palette.sunburst,
  },
  pickupListItem: {
    fontSize: "12px",
  },
  detailsContainerDivider: {
    background: palette.translucentWhite,
  },
  pickupList: {
    overflow: "scroll",
    margin: "0px",
    maxHeight: "240px",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  volumeContainer: {
    display: "flex",
    background: "white",
    borderRadius: "0.5rem",
    color: palette.typography.black.high,
    maxWidth: "5.5rem",
    padding: "0.1rem 0.5rem",
  },
  volumeColumnContainer: {
    display: "flex",
    justifyContent: "flexEnd",
    alignItems: "center",
    gap: "0.3rem",
    width: "3rem",
  },
  icon: {
    color: "white",
    height: "12px",
    width: "12px",
  },
  green: {
    backgroundColor: "#8AC149",
  },
});

export const PickupMarkerPopup = memo(
  ({
    postcode,
    pickupConfig,
    coordinates,
    handlePickupModalChange,
    onClose,
  }) => {
    const styles = useStyles();
    const pickupVolumes = useSelector(pickupVolumesSelector);

    const popupRoutes = Object.keys(pickupConfig.popupLabels).length;

    useEffect(() => {
      if (popupRoutes) return;
      handlePickupModalChange(false, { postcode });
      onClose();
    }, [popupRoutes, handlePickupModalChange, onClose, postcode]);

    const getPopupOffset = () => {
      const { length: routeCodesCount } = pickupConfig.routeCodes;
      if (routeCodesCount === 2) return 24;
      else if (routeCodesCount === 1 || routeCodesCount > 6) return 10;
      else return 38;
    };

    const renderPickupList = () => {
      const { routeCodes, popupLabels } = pickupConfig;

      return routeCodes
        .map((routeCode) => {
          const routeLabels = popupLabels[routeCode];
          if (!routeLabels) return [];

          const routeComponents = [
            <Text
              key={`${routeCode}`}
              className={styles.route}
            >{`Route ${routeCode}`}</Text>,
          ];

          Object.entries(routeLabels).forEach(
            ([postcode, postcodeLabel], index) => {
              const postcodeStops =
                pickupVolumes?.[routeCode]?.[postcode] || [];
              const postcodeParcels = getParcels({ stops: postcodeStops });

              index &&
                routeComponents.push(
                  <View
                    key={`${routeCode}-${postcode}-space`}
                    className={styles.postcodeSpace}
                  />
                );
              routeComponents.push(
                <Text
                  key={`${routeCode}-${postcode}`}
                  className={styles.postcode}
                >
                  {postcode}
                </Text>
              );
              routeComponents.push(
                !pickupVolumes ? (
                  <Text
                    key={`${routeCode}-${postcode}-partnerLabel`}
                    className={styles.pickupListItem}
                  >
                    {postcodeLabel}
                  </Text>
                ) : (
                  <View>
                    <Text
                      key={`${routeCode}-${postcode}-partnerLabel`}
                      className={styles.pickupListItem}
                    >
                      {postcodeLabel}
                    </Text>
                    <View className={styles.volumeContainer}>
                      <View className={styles.volumeColumnContainer}>
                        <LocationOnIcon
                          className={clsx(styles.icon, styles.green)}
                        />
                        {postcodeStops.length}
                      </View>
                      <View className={styles.volumeColumnContainer}>
                        <ParcelIcon
                          className={clsx(styles.icon, styles.green)}
                        />
                        {postcodeParcels}
                      </View>
                    </View>
                  </View>
                )
              );
            }
          );
          return routeComponents;
        })
        .flatMap((components) => components);
    };

    return (
      <View>
        {onClose && (
          <Popup
            coordinates={coordinates}
            anchor={"right"}
            offset={getPopupOffset()}
            className={styles.popup}
          >
            <View className={styles.popupContents}>
              <View className={styles.detailsContainerTopRow}>
                <Text>Pickup</Text>
                <CloseIcon
                  role="button"
                  onClick={() => {
                    handlePickupModalChange(false, { postcode });
                    onClose();
                  }}
                  className={styles.detailsContainerCloseIcon}
                />
              </View>
              <Divider
                className={styles.detailsContainerDivider}
                light={true}
              />
              <View className={styles.pickupList}>{renderPickupList()}</View>
            </View>
          </Popup>
        )}
      </View>
    );
  }
);
